import APITemplate from '@api/APITemplate'
import { diffTimeMinutes, formatQueryParams } from '@utils/Format'
import store from '@state/store'
import { MINUTES_TIMEOUT_REQUESTS } from '@constants/Numbers'
import con from '@services/connection'
import { subcategories } from '@modules/financial-planning/constants/URL'

function getQueryString(params) {
  const query = formatQueryParams(params || {})
  return query === '?' ? '' : query
}

class APIFinancialPlanningSubcategories extends APITemplate {
  constructor() {
    super()
    this.reqCarregar = null
  }

  _shouldSkipLoad(forceUpdate) {
    if (!forceUpdate) {
      const shouldUpdate = diffTimeMinutes(
        store.getters['financialPlanning/subcategoriesLastUpdate'],
        MINUTES_TIMEOUT_REQUESTS
      )

      if (!shouldUpdate) {
        return store.getters['financialPlanning/subcategories']
      }
    }

    if (store.getters['financialPlanning/subcategoriesLoading']) {
      return store.getters['financialPlanning/subcategories']
    }

    return null
  }

  _handleResponse(response) {
    if (response && response.status === 200) {
      const items = Array.isArray(response.value) ? response.value : []
      store.dispatch('financialPlanning/setSubcategories', items)
    } else {
      store.dispatch('financialPlanning/setSubcategories', [])
    }
  }

  async _executeCarregar(requestUrl) {
    store.dispatch('financialPlanning/setSubcategoriesLoading', true)
    this.reqCarregar = con.get(requestUrl)

    try {
      const response = await this.reqCarregar
      this._handleResponse(response)
      return response
    } catch (error) {
      this._handleResponse(null)
      throw error
    } finally {
      store.dispatch('financialPlanning/setSubcategoriesLoading', false)
      this.reqCarregar = null
    }
  }

  async carregar({ params = {}, forceUpdate = false } = {}) {
    const skipResult = this._shouldSkipLoad(forceUpdate)
    if (skipResult) return skipResult

    if (this.reqCarregar) {
      return this.reqCarregar
    }

    const query = getQueryString(params)
    const requestUrl = `${subcategories.list()}${query}`
    const response = await this._executeCarregar(requestUrl)

    return response || {}
  }

  async salvar({ subcategory }) {
    if (!subcategory || typeof subcategory !== 'object') {
      return {
        status: 400,
        message: 'Dados da subcategoria inválidos.',
      }
    }

    const payload = {
      data: JSON.stringify(subcategory),
    }

    const response = await con.post(subcategories.list(), payload)

    if (response && response.status === 200) {
      const value = Array.isArray(response.value)
        ? response.value[0]
        : response.value

      if (value && typeof value === 'object') {
        store.dispatch('financialPlanning/addSubcategory', value)
      } else {
        await this.carregar({ forceUpdate: true })
      }
    }

    return response || {}
  }

  async atualizar({ subcategoryId, subcategory }) {
    if (!subcategoryId) {
      return {
        status: 404,
        message: 'Subcategoria não encontrada.',
      }
    }

    if (!subcategory || typeof subcategory !== 'object') {
      return {
        status: 400,
        message: 'Dados da subcategoria inválidos.',
      }
    }

    const payload = {
      data: JSON.stringify(subcategory),
    }

    const response = await con.put(
      subcategories.detail(subcategoryId),
      payload
    )

    if (response && response.status === 200) {
      const value = Array.isArray(response.value)
        ? response.value[0]
        : response.value

      if (value && typeof value === 'object') {
        store.dispatch('financialPlanning/updateSubcategory', value)
      } else {
        await this.carregar({ forceUpdate: true })
      }
    }

    return response || {}
  }

  async remover({ subcategoryId }) {
    if (!subcategoryId) {
      return {
        status: 404,
        message: 'Subcategoria não encontrada.',
      }
    }

    store.dispatch('financialPlanning/setSubcategoriesLoading', true)

    const response = await con.delet(subcategories.detail(subcategoryId))

    store.dispatch('financialPlanning/setSubcategoriesLoading', false)

    if (response && response.status === 200) {
      store.dispatch('financialPlanning/removeSubcategory', subcategoryId)
    }

    return response || {}
  }

  async importar({ table, file, save = true } = {}) {
    const payload = { save }

    if (Array.isArray(table)) {
      payload.table = table
    }

    if (file) {
      payload.file = file
    }

    const response = await con.post(subcategories.batch(), payload)

    if (response && response.status === 200) {
      await this.carregar({ forceUpdate: true })
    }

    return response || {}
  }

}

const apiFinancialPlanningSubcategories =
  new APIFinancialPlanningSubcategories()
export { apiFinancialPlanningSubcategories }
